import type { ReactElement } from 'react';
import { useFontsQuery, useInvalidateFonts, useProductFontsQuery } from '@/entities/font';
import { allowedIdsKeyOf } from '../model/section-keys';
import { useSetProductFonts } from '../model/use-set-product-fonts';
import { SectionCard } from './section-card';
import { TextOptionAllowForm } from './text-option-allow-form';

/** 허용 해제 영향 — 정책 docs/products/custom-policy.md §3-4. */
const REMOVAL_IMPACT =
  '기존 디자인의 글자 모양은 그대로지만, 해제한 폰트를 쓴 디자인은 다른 폰트로 바꿔야 다시 저장할 수 있습니다.';

interface FontAllowSectionProps {
  readonly productId: string;
  readonly onSaved: (message: string) => void;
}

/**
 * 상품의 허용 폰트 — 고객 에디터가 이 상품에서 쓸 수 있는 폰트(결정 F2). 상품 상세에 허용 목록이 없어 따로 불러오고,
 * 정렬한 허용 id 를 key 로 폼을 리마운트해 저장 뒤 다시 불러온 서버 값을 반영한다. 다시 불러오기가 실패해도
 * 받아 둔 목록이 있으면 폼을 그대로 둔다 — 고르던 선택이 사라지지 않게.
 */
export const FontAllowSection = ({ productId, onSaved }: FontAllowSectionProps): ReactElement => {
  const invalidate = useInvalidateFonts();
  const fontsQuery = useFontsQuery();
  const allowedQuery = useProductFontsQuery(productId);
  const saveAction = useSetProductFonts({
    onSuccess: () => {
      onSaved('허용 폰트를 저장했습니다.');
      invalidate.forProduct(productId);
    },
    // 다른 화면에서 지운 폰트가 섞이면 400 이다 — 폰트 목록과 허용 목록을 다시 불러 지운 폰트를 걷어 낸다.
    onError: () => {
      invalidate.list();
      invalidate.forProduct(productId);
    },
  });

  const fonts = fontsQuery.data;
  const allowed = allowedQuery.data;
  const loadError = fontsQuery.error ?? allowedQuery.error;

  return (
    <SectionCard
      title="허용 폰트"
      description="고객 에디터에서 이 상품에 쓸 수 있는 폰트입니다. 폰트 목록은 기준 정보 관리에서 관리합니다."
      error={saveAction.error?.message ?? null}
    >
      {fonts !== undefined && allowed !== undefined ? (
        <TextOptionAllowForm
          key={allowedIdsKeyOf(allowed.map((font) => font.id))}
          options={fonts.map((font) => ({ id: font.id, label: font.familyName }))}
          initialIds={allowed.map((font) => font.id)}
          itemLabel="폰트"
          removalImpact={REMOVAL_IMPACT}
          isSaving={saveAction.isPending}
          onSave={(fontIds) => saveAction.setProductFonts({ productId, fontIds })}
        />
      ) : loadError !== null ? (
        <p role="alert" className="text-destructive">
          허용 폰트를 불러오지 못했습니다: {loadError.message}
        </p>
      ) : (
        <p className="text-muted-foreground">불러오는 중…</p>
      )}
    </SectionCard>
  );
};
